import { useRouter } from 'next/router';
import type { ViewProps } from './ui/View/View';
import { useSearchParams } from 'next/navigation';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'lodash';
import useEvent from './hooks/useEvent';
import { useHotkeys } from 'react-hotkeys-hook';
import { Box, DeleteButton } from './ui';
import { FilterBarCompletionsInput } from './ui/FilterBar/FilterBar';

export type ItemFilterValues = {
  freeFilter?: string;
};

type OrderFiltersProps = {
  onChange: (values: ItemFilterValues) => void;
  usesResetHotkey?: boolean;
  usesFindHotkey?: boolean;
  initialValue?: string;
  completions?: Record<string, string[]>;
  width?: string | number;
  queryParamNames?: string[];
} & ViewProps;

export const ItemFilters = ({
  onChange,
  usesResetHotkey,
  usesFindHotkey,
  initialValue,
  completions,
  width,
  queryParamNames = [],
}: OrderFiltersProps) => {
  const router = useRouter();
  const params = useSearchParams();

  const queryParamValue = useMemo(() => {
    if (!queryParamNames.length) return null;

    for (const paramName of queryParamNames) {
      const value = params.get(paramName);
      if (value) return value;
    }
    return null;
  }, [params, queryParamNames]);

  const debouncedChange = useMemo(() => debounce(onChange, 400), [onChange]);

  const [value, setValue] = useState(initialValue ?? '');

  const inputRef = useRef<HTMLInputElement | null>(null);

  const onChangeValue = useEvent((value: string) => {
    setValue(value);
    debouncedChange({ freeFilter: value });
  });

  const reset = useEvent(() => {
    setValue('');
    debouncedChange({ freeFilter: '' });
    if (queryParamNames.length && queryParamValue) {
      router.replace(router.pathname, undefined, { shallow: true });
    }
  });

  useEffect(() => {
    if (queryParamValue) {
      setValue(queryParamValue);
      debouncedChange({ freeFilter: queryParamValue });
    }
  }, [queryParamValue, debouncedChange]);

  useHotkeys(
    'esc',
    () => {
      if (usesResetHotkey) {
        reset();
      }
    },
    { enableOnFormTags: ['INPUT'] },
  );

  useHotkeys(
    'meta+f, ctrl+f',
    () => {
      if (usesFindHotkey) {
        inputRef.current?.focus();
      }
    },
    { preventDefault: true },
  );

  return (
    <Box flexDirection="row" position="relative" alignItems="center">
      <FilterBarCompletionsInput
        ref={inputRef}
        name="Filter"
        label="Filter"
        completions={completions}
        onChange={onChangeValue}
        inputProps={{
          width: width ?? '22em',
          value,
        }}
      />
      {value && (
        <DeleteButton
          position="absolute"
          marginBottom={1}
          bottom={2}
          right={12}
          onPress={reset}
        />
      )}
    </Box>
  );
};
